import { z } from 'zod';
// [P5b] The bounds are plain values and live in `constants/` so `pwa/` can read them without zod.
import { ATTENDANCE_STATES, DUTY_EVENT_ATTESTATIONS } from '../constants/dutyEvent.constant';
import { APP_DETAIL_INCLUDES, APP_DETAIL_VIEWS } from '../constants/officerApp.constant';
import { RAISER_LEVELS } from '../constants/raiserLevel.constant';
import { ARRANGEMENT_STATUSES, calendarDateSchema } from './arrangement.schema';
import { basePaginationSchema, objectIdSchema } from './common.schema';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE OFFICER SURFACE'S CONTRACT — `/app/*`. [P5a]
 *
 *  PA_REQUIREMENTS §5.4. This is what a constable's phone talks to, and it is a DIFFERENT
 *  contract from the dashboard's rather than a subset of it. Three things make that true,
 *  and each is a rule rather than a preference:
 *
 *  ⚠️ 1. A FORCE OFFICER'S PHONE NUMBER IS NOT IN THIS FILE, ANYWHERE.
 *
 *  Not `null`, not masked, not `.optional()`. The client confirmed that command phones are
 *  unlimited and FORCE phones must be audited and rate-limited — and a limit on a number
 *  that is already in the payload limits nothing: the audit would record "opened an order"
 *  and the counter would cap page loads. So the split is STRUCTURAL. `appForceOfficerSchema`
 *  has no phone field to fill in, and one number at a time comes from
 *  `GET /app/officers/:officerId/phone`, which is scoped, audited and metered.
 *
 *  ⚠️ A MASKED FORM WOULD BE WORSE THAN EITHER. "Last four digits" invites the next person
 *  to make it six, and there is no point on that slope with a reason attached to it.
 *
 *  ⚠️ 2. NEITHER OFFICER SHAPE MAY BE CALLED `*Summary*`, AND THAT IS NOT STYLE.
 *  `commandRoleIsolation.test.ts` CLAIM 5 bans the substring `sum` on any line that names
 *  command vocabulary, because `commandSum` is exactly the identifier it exists to catch. A
 *  line reading `commands: z.array(appCommandOfficerSummarySchema)` computes nothing and
 *  would still fail it — correctly, since the guard cannot read intent.
 *
 *  ⚠️ 3. NOTHING HERE COUNTS COMMAND. `namedCount` and `requiredCount` are FORCE totals
 *  (FR-5.2.6b, closing OD-9) and never share a line with a command entry.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

// ── Shared pieces ────────────────────────────────────────────────────────────────

/**
 * ⚠️ [C5c §5d] WHO RECORDED THE MARKING BEHIND AN OFFICER'S STATE — WHEN IT WAS NOT THEM.
 *
 * `attendanceAttestation` already says THAT a commander decided the state; this says WHO, because
 * §5e asks the app to point an officer marked absent at the commander rather than at the app, and
 * §5f lets that commander correct their own marking — `eventId` is what the correction route takes.
 *
 * ⚠️ PRESENT ONLY WHERE SOMEBODY OTHER THAN THE SUBJECT DECIDED IT. A state the officer settled
 * themselves carries no marker, whatever else a commander added beside it (`markerBehind`).
 *
 * ⚠️ AND DISCLOSED TO TWO READERS ONLY: the subject, about their own row, and the officers who hold
 * a command seat on THAT duty. A colleague on the roster and a nodal observer get nothing — which
 * commander marked whom is not a roster fact, and the audit log is where anybody else asks.
 *
 * `name` is nullable because a record can outlive its officer; `isMe` saves the app comparing ids.
 */
export const appAttendanceMarkerSchema = z.object({
  eventId: objectIdSchema,
  officerId: objectIdSchema,
  name: z.string().nullable(),
  isMe: z.boolean(),
});

/**
 * One officer as the app renders them — WITHOUT a phone number. See rule 1 above.
 *
 * ⚠️ [P5a §6] `rankTitle` IS `ranks.name` AND `rankShortName` IS `ranks.short_name`.
 * The rank master has no `title` column at all, which is what the supplied contract named.
 * Stated here because a mapping that is obvious to whoever writes it is the mapping that
 * gets reversed by whoever maintains it: `rankTitle` is the full "Sub-Inspector",
 * `rankShortName` is the "SI" a roster prints.
 */
export const appForceOfficerSchema = z.object({
  id: objectIdSchema,
  name: z.string(),
  rankTitle: z.string().nullable(),
  rankShortName: z.string().nullable(),
  orgUnitName: z.string().nullable(),
  /**
   * ⚠️ [C5 §6] THE DIVISION ABOVE THE POSTING — see `orderSheetOfficerSchema.orgUnitDivisionName`,
   * which gained the same field in the same pass for the same reason. An officer reading a
   * roster on their phone sees `Nadial` beside a name and cannot tell whose Nadial it is.
   *
   * ⚠️ SENT RATHER THAN DERIVED, because `pwa` cannot walk an org tree: it holds no unit
   * collection, and §6 forbids two clients deriving one ancestor separately.
   */
  orgUnitDivisionName: z.string().nullable(),
  photoUrl: z.string().nullable(),
  /** True for the caller's own row, so the app can pin it to the top without comparing ids. */
  isMe: z.boolean(),
  /**
   * ⚠️ [P5a §4c] PRESENT ONLY ON THE CALLER'S OWN ROW, AND IT IS FOR P5c.
   *
   * Start Duty, Mark Reached and Stand Down are per ASSIGNMENT (FR-5.4.4), so the app needs
   * the id of the row it is about to write an event against. Adding it now costs one field;
   * adding it later is a contract change for a team already building against this.
   */
  assignmentId: objectIdSchema.nullable(),
  /**
   * ⚠️ [amended P5c] ADDITIVE, AND IT PROPAGATES TO COMMAND ROWS ON PURPOSE.
   *
   * PA_REQUIREMENTS Appendix B's machine, derived on every read and stored nowhere.
   * `appCommandOfficerSchema` EXTENDS this object, so a command row needs a value — and
   * `EXEMPT` is the true one (FR-5.4.7, generalised to every per-policy command role).
   * One field, correct for both rosters, rather than a flag saying which shape you have.
   *
   * ⚠️ IT IS ABSENT UNDER `view=skeleton`, because the whole roster is. Read
   * `myAttendance` at the duty root for the caller's own state — that one survives, for
   * the reason `myAssignmentId` does.
   */
  attendance: z.enum(ATTENDANCE_STATES),
  /**
   * ⚠️ [amended C1c §2d] WHO SAYS SO — A SECOND AXIS, NOT A SIXTH STATE.
   *
   * `SELF` when the officer tapped it on their own device; `ON_BEHALF` when a commander
   * tapped it for them, so the position and the clock are the COMMANDER's; `INFERRED` when
   * the server derived it from another marking (§2b's coupling). `null` where no marking
   * decided the state at all — silence, and `EXEMPT`.
   *
   * ⚠️ THE STATE IS UNCHANGED BY IT. A commander-attested `REACHED` is still `REACHED`,
   * because the officer IS present; adding a member to `ATTENDANCE_STATES` would have forced
   * every existing attendance query to be re-read, which is [LIFECYCLE]'s whole argument for
   * a second column over a second status.
   *
   * ⚠️ REQUIRED KEY, NULLABLE VALUE — the shape `rank` and `orgUnit` use. The roster renders
   * a labelled row either way, so `null` answers the question the screen is asking.
   */
  attendanceAttestation: z.enum(DUTY_EVENT_ATTESTATIONS).nullable(),
  /**
   * ⚠️ [C5c §5d] OPTIONAL, NOT NULLABLE — [P6d]'s test: a caller who is not entitled to it renders
   * nothing, so the key's presence is the signal and every other payload is unchanged. See
   * `appAttendanceMarkerSchema` for who is.
   */
  attendanceMarkedBy: appAttendanceMarkerSchema.optional(),
});

/**
 * The same officer, PLUS the phone — for a command role only.
 *
 * ⚠️ THIS IS THE ONLY SCHEMA IN THE OFFICER SURFACE THAT CARRIES A NUMBER INLINE, and it is
 * a deliberate, client-confirmed asymmetry: an officer must be able to ring their Incharge
 * without asking anybody's permission, and there are a handful of them per duty rather than
 * two hundred. FR-5.4.3's Call action is what this exists for.
 */
export const appCommandOfficerSchema = appForceOfficerSchema.extend({
  phone: z.string(),
});

/** One stop on a route, or the single place of a concurrent duty. */
export const appLocationSchema = z.object({
  /**
   * ⚠️ [P5a §4d] A STABLE ID, MINTED AT PUBLISH — NOT AN INDEX INTO THE ARRAY.
   *
   * P5c marks reached PER STOP (FR-5.4.4), and two stops on one route can legitimately share
   * a name. A position would be the fourth identity in this system derived from where a row
   * SITS rather than from what it IS, and the other three all had to be corrected.
   */
  id: z.string(),
  name: z.string(),
  note: z.string().nullable(),
  displayOrder: z.number().int(),
});

export const appInstructionSchema = z.object({ type: z.literal('LINE'), text: z.string() });
export const appSupportingArrangementSchema = z.object({
  personName: z.string(),
  responsibilities: z.array(z.string()),
});

// ── The list — GET /app/arrangements ─────────────────────────────────────────────

export const listAppDutiesQuerySchema = basePaginationSchema.omit({ sortOrder: true }).extend({
  dateFrom: calendarDateSchema.optional(),
  dateTo: calendarDateSchema.optional(),
  /**
   * ⚠️ 50, NOT `PAGINATION.MAX_LIMIT`'s 100. Each item carries a resolved post label and an
   * incharge, so a page is several batched reads wide rather than one row deep.
   */
  limit: z.coerce.number().int().min(1).max(50).default(20),
});

/**
 * ⚠️ THERE IS NO `sortBy`, AND THAT IS THE DECISION. "My duties" has exactly one useful
 * order — `duty_date` then `starts_at`, which is chronology — and the index that serves it
 * is the officer surface's only index. A sort allowlist here would need one index per value
 * (§7) on the collection that takes the heaviest write in the system.
 */

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C5c §2i] A DUTY POINT THE CALLER IS COVERED AT, BY A COLUMN SHARED FROM ANOTHER.
 *
 *  An inspector shared across five duty points holds ONE assignment — on the ANCHOR card's duty
 *  — because that is what makes them one count rather than five. The client's rule is that the
 *  officer still sees every duty point separately (§2i), so the other four reach the officer as
 *  rows DERIVED from that assignment on every read, and this names where the marking lives.
 *
 *  ⚠️ §2j: ONLY THE ANCHOR DUTY IS MARKABLE. A covered row carries `assignmentId: null` and a
 *  MIRRORED `myAttendance`, and a marking posted on its deployment is a 404 — there is no
 *  assignment there to write against, so no covered duty can ever derive the phantom absence the
 *  brief forbids.
 *
 *  ⚠️ OPTIONAL, AND ITS PRESENCE IS THE SIGNAL ([P6d]): a row without it is an ordinary seat,
 *  and a payload with no shared column anywhere is byte-for-byte what it always was.
 *
 *  ⚠️ `displayOrder` IS 0-BASED, like `appDutyPointSchema.displayOrder` — a client renders
 *  `DP{displayOrder + 1}` on both, so a covered row and its anchor section agree.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const appSharedFromSchema = z.object({
  /** The anchor DUTY — where the officer's own marking lives, and what a link should open. */
  deploymentId: objectIdSchema,
  dutyPointId: z.string(),
  dutyPointTitle: z.string(),
  displayOrder: z.number().int().min(0),
});

/**
 * ⚠️ [P7A §3] ONE VEHICLE A DUTY REQUIRES. `lineKey` is the same identity the requirement line carries
 * (`V:<vehicleTypeId>`), `label` the vehicle type's name, `requiredCount` the line's own figure.
 * There is no vehicle mapping — no unit is told to name a van — so nothing else is said about it.
 */
export const appVehicleSchema = z.object({
  lineKey: z.string(),
  label: z.string(),
  requiredCount: z.number().int().min(0),
});

export const appDutyListItemSchema = z.object({
  deploymentId: objectIdSchema,
  arrangementId: objectIdSchema,
  /**
   * ⚠️ [P5a §6] EIGHT HEX CHARACTERS, NOT AN ObjectId AND NOT `dp_`-PREFIXED. The supplied
   * contract's `"dp_a1b2c3d4"` is not a shape this system produces — `mintedId.util.ts`
   * mints `randomBytes(4).toString('hex')`. Contracts get copied literally, so it is stated.
   */
  dutyPointId: z.string(),
  reference: z.string(),
  /**
   * ⚠️ [C2b §8b] THE DEPARTMENT'S OWN NUMBER, WHICH IS WHAT AN OFFICER IS TOLD ON THE GROUND.
   *
   * A supervisor says "you are on PA 5678", not "you are on PA-2026-0142". The officer app
   * showed only the minted reference, so the two could not be matched up on a phone.
   *
   * ⚠️ NULLABLE AND REQUIRED. It is operator-typed and absent on plenty of orders, and every
   * renderer of it falls back to `reference` — so a caller must be handed something to decide
   * with rather than an absent key. [P6d]'s optional-key rule is about a field whose PRESENCE
   * is the signal; this one's VALUE is.
   */
  paNumber: z.string().nullable(),
  title: z.string().nullable(),
  /**
   * ⚠️ [P5a §6] THE REAL ENUM. The supplied contract named `ISSUED`, which is not a value in
   * this system and never has been — `ARRANGEMENT_STATUSES_ISSUED` is a SUBSET TUPLE meaning
   * "somebody has been told about this order", and a tuple name is not a status.
   *
   * `CANCELLED` reaches an officer deliberately: archiving an issued order cancels it, and
   * the officer who was told to report is the person who most needs to know it is off.
   */
  status: z.enum(ARRANGEMENT_STATUSES),
  /**
   * ⚠️ [P7A §2] THE LEVEL THE ORDER WAS RAISED AT — `HQ` · `DIVISION` · `UNIT`, or `null` (render
   * no pill). Resolved ONCE on the server from the raiser's effective unit, by position; a client
   * holds no `ancestors` and must never derive it. Duty Mitra shows HQ / DRO / Unit.
   *
   * ⚠️ REQUIRED-NULLABLE, by `reportingAt`'s test above: every row renders a pill slot either way,
   * so `null` answers a question the screen is asking. A constable's payload gains a key — stated.
   */
  raisedAtLevel: z.enum(RAISER_LEVELS).nullable(),
  dutyPointTitle: z.string(),
  dutyDate: calendarDateSchema,
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  hasFixedTime: z.boolean(),
  spansMidnight: z.boolean(),
  slotLabel: z.string(),
  /**
   * ⚠️ [C3a §6] THE REPORTING INSTANT ON A LIST ROW — required, nullable, and it is a CONTRACT
   * CHANGE the native app team must be told about.
   *
   * `appDutySchema` (the DETAIL) has carried `reportingAt` since [C1c §1]; the LIST never did, so
   * `DutyCard` and `UpcomingRow` could only ever say `No fixed time` for a duty whose reporting
   * time the system knew. §6 is what makes that field mean something on the two screens an
   * officer actually looks at.
   *
   * ⚠️ REQUIRED-NULLABLE RATHER THAN OPTIONAL, WHICH IS THE OPPOSITE OF `dutyDateResolved`'s
   * CALL. [P6d]'s test is whether a caller who will never receive a value has to render
   * something: there, the field's PRESENCE was the signal a different code path ran; here every
   * row renders a time slot either way, and `null` is the answer "this duty has no reporting
   * time". A constable's payload does gain a key — stated rather than hidden.
   *
   * ⚠️ [amended C5b §3e] `startsAt` on a fixed-time duty — see `appDutySchema.reportingAt`.
   */
  reportingAt: z.iso.datetime().nullable(),
  /**
   * [STAND-DOWN] WHEN A WHOLE-DAY DUTY STANDS DOWN — an INSTANT, required, nullable, and a CONTRACT
   * CHANGE. `null` on every shift duty (its end is `endsAt`). With `reportingAt` it is the duty's
   * stated window; Duty Mitra does not render it yet.
   */
  standDownAt: z.iso.datetime().nullable(),
  places: z.array(appLocationSchema),
  reportingPlace: appLocationSchema.nullable(),
  /** The post this officer fills — the label of their requirement LINE, resolved server-side. */
  myPost: z.string().nullable(),
  /**
   * ⚠️ [amended P5c] NULLABLE, BECAUSE A COMMAND OFFICER IS NOW ON THIS LIST.
   *
   * [P5a] made the officer surface's isolation the ASSIGNMENT, so a commander who held no
   * force slot saw no duties at all. P5c widens it to "an assignment OR a command seat",
   * and a command seat has no assignment id — `assignments` deliberately knows nothing
   * about command. `null` therefore means "you are on this duty as COMMAND", which is the
   * one thing a client needs in order to pick the right write endpoint.
   */
  assignmentId: objectIdSchema.nullable(),
  /** ⚠️ [amended P5c] The caller's OWN state. Survives `view=skeleton`; the roster does not. */
  myAttendance: z.enum(ATTENDANCE_STATES),
  /**
   * ⚠️ FORCE ONLY. Command is excluded, FR-5.2.6b. ⚠️ [amended P7A §3] AND PEOPLE ONLY — a vehicle is
   * never in either figure, so a duty that read `12/14` because of a van reads `12/12`.
   */
  forceSummary: z.object({ namedCount: z.number().int(), requiredCount: z.number().int() }),
  incharge: appCommandOfficerSchema.nullable(),
  /** ⚠️ [C5c §2i] Present only on a row the caller is COVERED at — see `appSharedFromSchema`. */
  sharedFrom: appSharedFromSchema.optional(),
});

// ── The detail — GET /app/arrangements/:id ───────────────────────────────────────

/**
 * ⚠️ `deploymentId` IS OPTIONAL, AND THE PAYLOAD IS ONE SHAPE EITHER WAY.
 *
 * Supplied, the answer is one duty point holding one duty — the sheet for one night, which
 * is what the list links to and what P5c's actions are about. Omitted, it is the whole order
 * with every duty the caller is named on, which is what "show me this bandobast" means.
 *
 * Two payload shapes for one screen would mean the app parsing a discriminated union to
 * render the same thing, so the filtered form is simply the general one with one member in
 * each array.
 */
/**
 * ⚠️ [amended P5b] `view=skeleton` OMITS THE ROSTERS AND KEEPS THE SHAPE. BOTH HALVES MATTER.
 *
 * WHY IT EXISTS. The note above bounds the DUTIES and says so; what it also says is that it
 * does not bound the roster they multiply — a 200-strong order across 60 duties is 12,000
 * officer rows in one payload. The officer app renders a section per duty date and opens one,
 * so it needs the order's shape immediately and one section's names on demand. Without a
 * roster-free mode, lazy-loading per section is impossible: the first request already carries
 * everything.
 *
 * WHY IT IS NOT A SECOND PAYLOAD SHAPE. The rule above — one shape either way, no
 * discriminated union to parse — is kept, not repealed. `force` and `commands` are the same
 * arrays they always were and arrive EMPTY, and one boolean at the root says which question
 * an empty array is answering. A client branches on `rostersIncluded`, never on a type.
 *
 * ⚠️ AND THE BOOLEAN IS NOT DECORATION. `force: []` alone is ambiguous with "nobody is named
 * yet", which is a real and common state — every order between publish and the first fill
 * looks exactly like that. A screen that cannot tell "not loaded" from "nobody" renders
 * "0 of 40" where it should render a spinner, and an officer reads it as nobody turning up.
 *
 * ⚠️ THE DEFAULT IS `full`, DELIBERATELY. The native app team is days into building against
 * the published contract; a new mode that has to be asked for breaks nobody, and a new
 * default would break everybody silently.
 *
 * The counts survive in both views — see `deploymentFillCounts` at the service — so the
 * section header states "12 of 40 named" before its names have been fetched.
 */
export const appDutyDetailQuerySchema = z.object({
  deploymentId: objectIdSchema.optional(),
  view: z.enum(APP_DETAIL_VIEWS).default('full'),
  /**
   * ⚠️ [P6d] ONE IST DUTY DATE. It NARROWS an existing answer and never widens one.
   *
   * On the caller's own duties it is a plain filter and is absent by default, so a client that
   * has never heard of it gets exactly what it always got. On the OPERATIONS view — a nodal
   * reading an order through their org scope — it is what makes the response bounded at all:
   * that set is every duty on the order rather than the handful the caller stands, and a
   * month-long three-shift bandobast is several hundred.
   *
   * Absent there, the server resolves one and echoes it as `dutyDateResolved`. See
   * `officerDuty.service.ts`.
   */
  dutyDate: calendarDateSchema.optional(),
  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [P6d] WHICH DUTIES — MINE, OR THE ORDER'S. IT IS EXPLICIT BECAUSE THE ANSWER CANNOT
   *  BE INFERRED FROM WHO IS ASKING.
   *
   *  A nodal is often ALSO on a roster (§2c is emphatic that they must not need a laptop to
   *  see their own posting), so "does this caller hold a seat" decides entitlement and decides
   *  NOTHING about which of two questions they are asking. Inferred from the seat, a nodal who
   *  opened the operations view of an order they happen to be standing would have been shown
   *  their own duty and none of the order's other duty points — which is [P6b.2]'s defect
   *  exactly: "nothing at this place" and "this screen does not show that place" looking alike.
   *
   *  ⚠️ `order` REQUIRES THE SCOPE ENTITLEMENT AND IS OTHERWISE IGNORED, not refused. A caller
   *  without it has no order-wide view to be given, and 422-ing a parameter that merely cannot
   *  widen anything would turn a harmless default into an error for every client that adopts
   *  the key before its role does.
   *
   *  ⚠️ AND THE DEFAULT IS `mine`, so every request written before this pass — every request an
   *  officer's app makes — is byte-for-byte the one it always was.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  include: z.enum(APP_DETAIL_INCLUDES).default('mine'),
});

/** One duty the caller is named on, with the full roster for it — FR-5.4.2. */
export const appDutySchema = z.object({
  deploymentId: objectIdSchema,
  dutyDate: calendarDateSchema,
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  hasFixedTime: z.boolean(),
  spansMidnight: z.boolean(),
  slotLabel: z.string(),
  places: z.array(appLocationSchema),
  reportingPlace: appLocationSchema.nullable(),
  /** The caller's own assignment on this duty — what P5c writes a duty event against. */
  /** ⚠️ [amended P5c] `null` when the caller is on this duty as COMMAND — see the list item. */
  myAssignmentId: objectIdSchema.nullable(),
  /**
   * ⚠️ [amended P5c] THE CALLER'S OWN STATE, AT THE DUTY ROOT, AND IT IS NOT A DUPLICATE.
   *
   * `force[].officers[]` carries `attendance` per officer, which is what a supervisor
   * decides from — but `view=skeleton` empties that array, and the collapsed section is
   * exactly where an officer needs to see whether they have started. This field survives a
   * skeleton for the reason `myAssignmentId` does.
   *
   * ⚠️ TWO EXPRESSIONS OF ONE VALUE, SO A TEST HOLDS THEM TOGETHER. `officerDuties.test.ts`
   * asserts this equals the caller's own row inside `force` on a full read — [D4.5]'s rule,
   * the same one that pins list, skeleton and full against each other on the counts.
   */
  myAttendance: z.enum(ATTENDANCE_STATES),
  /**
   * ⚠️ [C5c §5e] WHO RECORDED THE CALLER'S OWN STATE, WHEN IT WAS NOT THE CALLER — AND IT
   * SURVIVES A SKELETON, for `myAttendance`'s reason.
   *
   * A commander's absence is FINAL against the officer: their own Start Duty after it is recorded
   * and the absence stands. An officer who arrives late, taps Start Duty and sees nothing change is
   * the worst version of that, so the app names the commander from this and points the officer at
   * them rather than at the app. Optional, for [P6d]'s reason: absent means nobody else decided it.
   *
   * ⚠️ TWO EXPRESSIONS OF ONE VALUE on a full read, with the caller's own row's `attendanceMarkedBy`
   * — `officerDuties.test.ts` holds them together, `myAttendance`'s precedent.
   */
  myAttendanceMarkedBy: appAttendanceMarkerSchema.optional(),
  /**
   * ⚠️ [C5c §2i] Present only on a duty the caller is COVERED at — see `appSharedFromSchema`. Its
   * `myAttendance` then MIRRORS the anchor duty's, and `myAttendanceMarkedBy` names whoever
   * recorded that state there.
   */
  sharedFrom: appSharedFromSchema.optional(),
  /**
   * ⚠️ [amended C1c §2b] WHICH STOPS THE CALLER HAS ALREADY REACHED — AND THE COUPLING IS WHY
   * IT HAD TO EXIST.
   *
   * Until C1c the officer app was the ONLY writer of a `REACHED`, so it could render its
   * per-stop controls from its own pending queue and be right. Start Duty now also records a
   * `REACHED` at the FIRST stop, server-side, so an app reading only its own queue would keep
   * offering a control for a stop already recorded — and the tap would come back 409, an error
   * for something the app itself offered.
   *
   * ⚠️ IT SURVIVES `view=skeleton`, for `myAttendance`'s reason: the per-stop list is part of
   * the ladder, and the ladder renders before a single name has been fetched.
   *
   * ⚠️ `''` IS A REAL MEMBER, not a bug. A single-stop duty and a duty stamped before P5a both
   * carry `location_id: null`, and `onceKeyOf` already spells that `REACHED#`. A client
   * comparing against `place.id` will simply never match it, which is correct — those duties
   * have no per-stop controls to suppress.
   */
  myReachedLocationIds: z.array(z.string()),
  /**
   * ⚠️ [amended C1c §1a, §2c] WHEN THE FORCE IS TO ASSEMBLE — AN INSTANT, NOT `HH:mm`.
   *
   * The duty point holds a wall-clock string; the DUTY holds it resolved on its own date, in
   * IST, by the same crossing that produced `startsAt`. An instant is what reaches the app for
   * two reasons: this package cannot import luxon, so a client-side crossing would be a third
   * expression of `istDutyDate`; and §2c's marking window opens at the EARLIER of this and
   * `startsAt`, which is a comparison of numbers rather than a calendar computation.
   *
   * ⚠️ INDEPENDENT OF `reportingPlace` (§1d) — either may be `null` while the other is set, so
   * a screen must render a time with no place and a place with no time.
   *
   * ⚠️ [amended C5b §3e] ON A FIXED-TIME DUTY IT **IS** `startsAt`, derived by the server on every
   * read (`reportingInstantOf`). It used to be the card's earliest shift start, stamped onto every
   * duty of the card — a 17:00 duty carried 05:30 — so §2c's window opened hours before the shift.
   * Only a whole-day duty carries a reporting time of its own.
   */
  reportingAt: z.iso.datetime().nullable(),
  /** [STAND-DOWN] The stamped stand-down instant, beside `reportingAt`. `null` on a shift duty. */
  standDownAt: z.iso.datetime().nullable(),
  myPost: z.string().nullable(),
  /**
   * FR-5.4.2 — every post on the duty and the names against it, in the order the CP typed
   * them. `requiredCount` is stated so the app derives "3 of 4" itself rather than being
   * sent a percentage it cannot recompute.
   *
   * ⚠️ [amended P7A §3] POSTS AND ONE-OFF COLUMNS ONLY — A VEHICLE IS NEVER HERE. A van is counted and
   * never named, so a group for it read "0 of 2 named" with a source still to come on every order
   * carrying one. It is in `vehicles` below, with its required count and nothing else, and it is in
   * none of this duty's figures: not a group, not `namedCount`, not `requiredCount`.
   */
  force: z.array(
    z.object({
      lineKey: z.string(),
      label: z.string(),
      requiredCount: z.number().int(),
      namedCount: z.number().int(),
      /**
       * ─────────────────────────────────────────────────────────────────────────────
       *  ⚠️ [C5 §7c] WHICH DIVISIONS OR UNITS ARE OUTSTANDING, BESIDE `N of M named`.
       *
       *  The count said HOW MANY were missing and nothing said WHO owed them — the same
       *  information the printed order gives per source, on the surface a nodal actually
       *  carries. Without it "12 of 40" is a number to act on with no idea whom to ring.
       *
       *  ⚠️ IT IS THE PLAN'S OWN GRAIN — one entry per allocation row, in the order the
       *  operator arranged them, INCLUDING the ones that are fully supplied. Sending only the
       *  short ones would make "this source is done" and "this source was never asked"
       *  indistinguishable, which is [P6b.2]'s defect and the reason the printed sheet prints
       *  every block too.
       *
       *  ⚠️ `label` IS RESOLVED SERVER-SIDE, `Division · Unit`. `pwa` holds no org tree and §6
       *  forbids two clients deriving one ancestor separately.
       *
       *  ⚠️ AND IT ADDS NO OFFICER AND NO ID. This is a COUNT table beside a roster that
       *  already names everybody the caller may see; a second list of people would be a second
       *  answer to who is on the duty.
       * ─────────────────────────────────────────────────────────────────────────────
       */
      sources: z.array(
        z.object({
          allocKey: z.string(),
          label: z.string(),
          askedCount: z.number().int().min(0),
          namedCount: z.number().int().min(0),
          /**
           * ─────────────────────────────────────────────────────────────────────────
           *  ⚠️ [P7A §4] THIS SOURCE CLOSED ITS SHARE SHORT, AND WHY — NOT ASSIGNED IS NOT SHORT.
           *
           *  Non-null iff the source's ADDRESSEE declared its share short and is still short of
           *  people on this duty: it is `shortfallNotesFor`'s DECLARED note, the one builder behind
           *  the preview's notes and the printed order's, so a share filled after its close reads
           *  `null` exactly as the paper drops its note. `askedCount - namedCount` says how many are
           *  missing; this says whether anybody can still be chased for them.
           *
           *  ⚠️ RENDER IT ONLY ON A SOURCE THAT IS STILL SHORT (`askedCount > namedCount`), under
           *  `Closed short`, and NEVER under `Still to come` — a division that has replied is not one
           *  to ring. A source of the same addressee that is full on this line carries the reason too
           *  and has nothing to state.
           *
           *  ⚠️ A REQUIRED KEY WITH A NULLABLE VALUE, [C5 §7d]'s argument: an older server omitting
           *  it must not read as an open gap. A vehicle is never a source here (`vehicles` below), so
           *  no vehicle carries a reason. It is readable by anyone who can open the order — what
           *  printing it on the order already means ([C4a §7]). The words never say who declared.
           * ─────────────────────────────────────────────────────────────────────────
           */
          closedShortReason: z.string().min(1).max(300).nullable(),
        }),
      ),
      officers: z.array(appForceOfficerSchema),
    }),
  ),
  /**
   * ⚠️ [P7A §3] THE VEHICLES THIS DUTY REQUIRES — the count only, never a name, a named count or a gap.
   * Required, possibly `[]`, and present on a skeleton too: it is the requirement, not a roster. A
   * vehicle asking for nothing is absent, `force`'s own zero rule. In the order the CP typed them.
   */
  vehicles: z.array(appVehicleSchema),
  /** ⚠️ [amended P7A §3] People only — both figures leave every vehicle out. */
  namedCount: z.number().int(),
  requiredCount: z.number().int(),
  /**
   * Every entry STAMPED on this duty, in policy role order — including several Incharges and the
   * one-off rows that name themselves.
   *
   * ⚠️ [amended C6a] "INCLUDING THE UNFILLED ONES WITH `officer: null`" STOOD HERE AND WAS FALSE:
   * `stampCommandForSlot` drops a role nobody is named to, so an unfilled BY-NAME role is simply
   * absent. What C6a adds is the one case that is not: a role ASKED OF A DIVISION and still awaited
   * on this duty appears once, with `officers: []` (or the names given so far) and `awaitedFrom`.
   */
  commands: z.array(
    z.object({
      roleKey: z.string().nullable(),
      label: z.string(),
      officers: z.array(appCommandOfficerSchema),
      /**
       * ⚠️ [C6a] WHO STILL OWES THIS ROLE ON THIS DUTY — `['CD']`, `['CD · TALA']`. OPTIONAL and
       * omitted when nothing is awaited, so every duty filled by name is byte-identical to before
       * ([P6d]'s test: the key's presence is the signal). Absent on a skeleton, as every roster is.
       * Words, never a number (FR-5.2.6b).
       */
      awaitedFrom: z.array(z.string()).optional(),
    }),
  ),
});

export const appDutyPointSchema = z.object({
  dutyPointId: z.string(),
  title: z.string(),
  /**
   * ⚠️ [C5 §4c] THE OPERATOR'S OWN CARD NUMBER, SO `DP1` MEANS THE SAME THING ON BOTH SURFACES.
   *
   * `ArrangementView` renders `DP{displayOrder + 1}`, the printed sheet numbers from
   * `orderSheetDutyPointSchema.ordinal`, and the preview modal does the same — so a phone that
   * numbered its cards `index + 1` would disagree with all three the moment a constable's
   * payload omits a card they are not on, which is the ordinary case rather than an edge one.
   * The server already HAD this value and stripped it: `officerDuty.service.ts` sorted on
   * `ordinal` and then mapped it away.
   *
   * ⚠️ IT ADDS ONE INTEGER PER CARD TO A CONSTABLE'S PAYLOAD, WHICH [P6d] SAID MUST NOT
   * HAPPEN — and [C4a §3] already reversed that rule by client decision. Recorded in
   * `docs/api/officer-contract.md` rather than slipped in: the native app team builds against
   * that file.
   *
   * ⚠️ REQUIRED, NOT OPTIONAL. §6's test is whether a caller who receives no value has to
   * render something: every card shows its number, so there is no "the other path ran" signal
   * for `undefined` to carry.
   */
  displayOrder: z.number().int().min(0),
  /** ⚠️ [D4.3] THE SPECIFIC ONES — this card's own standing orders, not the order's. */
  instructions: z.array(appInstructionSchema),
  supportingArrangements: z.array(appSupportingArrangementSchema),
  duties: z.array(appDutySchema),
});

export const appArrangementDetailSchema = z.object({
  arrangementId: objectIdSchema,
  reference: z.string(),
  paNumber: z.string().nullable(),
  title: z.string().nullable(),
  description: z.string().nullable(),
  status: z.enum(ARRANGEMENT_STATUSES),
  /**
   * ⚠️ [P7A §2] THE LEVEL THE ORDER WAS RAISED AT — `appDutyListItemSchema.raisedAtLevel`'s fact
   * and derivation; on `include=order` it is what the Operations detail header draws.
   *
   * ⚠️ REQUIRED-NULLABLE, AND A CONSTABLE'S DETAIL PAYLOAD GAINS THIS KEY — REVERSING [P6d]'s "a
   * constable's responses do not change" (CLAIM 3's root-key pin) FOR THIS ONE KEY, on the client's
   * decision that every order says who raised it. By CLAUDE.md §6's own test it is nullable rather
   * than optional: a caller who never receives a level still renders a pill slot, and `null` is the
   * answer "draw nothing". `dutyDateResolved` below stays optional — its PRESENCE is its signal.
   */
  raisedAtLevel: z.enum(RAISER_LEVELS).nullable(),
  paDate: calendarDateSchema,
  /**
   * ⚠️ [P5a §4b] THE PA-LEVEL ONES, WHICH THE SUPPLIED CONTRACT OMITTED ENTIRELY.
   *
   * [D4.3] §2 deliberately put instructions and supporting arrangements at BOTH levels —
   * general on the order, specific on the duty point. A contract carrying only the duty
   * point's half means the general standing orders for a bandobast never reach the people
   * standing it, which is the one thing this screen exists to prevent.
   */
  instructions: z.array(appInstructionSchema),
  supportingArrangements: z.array(appSupportingArrangementSchema),
  /**
   * ⚠️ [amended P5b] WHETHER `force` AND `commands` BELOW WERE LOADED AT ALL.
   *
   * `true` for `view=full` (the default), `false` for `view=skeleton`. Read it before
   * rendering a count off `officers.length`: on a skeleton those arrays are empty because
   * nobody asked for them, not because nobody is named. `namedCount` and `requiredCount`
   * are populated either way and are what a header should show.
   *
   * A section's roster is then fetched with `?deploymentId=…` (which is always `full`) and
   * merged in. Two responses about one duty always agree, because both counts come from the
   * same rollup the list uses.
   */
  rostersIncluded: z.boolean(),
  /**
   * ⚠️ [P6d] WHICH DUTY DATE THIS RESPONSE IS ABOUT — PRESENT ONLY WHEN ONE WAS APPLIED.
   *
   * ⚠️ `.optional()` RATHER THAN `.nullable()`, WHICH REVERSES THIS FILE'S OWN PREFERENCE, AND
   * THE REASON IS AN EXIT CRITERION RATHER THAN A STYLE CALL. `serviceNumber` and `division` on
   * `officerIdentitySchema` are nullable because a profile renders a labelled row either way,
   * so `null` answers a question the screen is asking. This one is different: a required key
   * carrying `null` would appear in every constable's payload, and P6d's rule is that a
   * constable's responses do not change. `undefined` is omitted by `JSON.stringify`, so on the
   * unbounded path the bytes are byte-for-byte what they were.
   *
   * Present ⇒ the `duties` arrays hold that date only, and the app should say so. Absent ⇒ every
   * duty the caller is on, which is what this endpoint has always answered.
   */
  dutyDateResolved: calendarDateSchema.optional(),
  dutyPoints: z.array(appDutyPointSchema),
});

// ── The phone reveal — GET /app/officers/:officerId/phone ────────────────────────

export const revealPhoneParamsSchema = z.object({ officerId: objectIdSchema });

/**
 * ⚠️ SCOPED TO THE DUTY, NOT TO THE ORDER — AND THAT IS THE WHOLE CONTROL.
 *
 * An officer on Monday must not be able to read Wednesday's roster, so the question is
 * "were you both on THIS duty", which is a deployment. Required, never defaulted: a
 * defaultable scope is a scope somebody widens.
 */
export const revealPhoneQuerySchema = z.object({ deploymentId: objectIdSchema });

export const revealedPhoneSchema = z.object({
  officerId: objectIdSchema,
  name: z.string(),
  /** Ten digits. The `+91` belongs to the DIALER, never to storage (FR-5.8.3c). */
  phone: z.string(),
});
